import './menu-item-cart-card.css';

import { EventEmitter } from "eventemitter3";
import { Order } from '../../../models/order';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { Util } from '../../../utils/util';
import { IName } from '../../../interfaces/global.interfaces';
import { Data } from '../../../models/data';
import { IOrderCoupon, IOrderItem, IOrderItemCoupon } from '../../../models/order.interface';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { ICustomizeItemProps } from '../../modals/CustomizeItem/customize-item.interface'
import { DataLayerService } from '../../../services/data-layer-service';
import { IDataItem } from '../../../models/data.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { EventTypes } from '../../../core/event-bus';
import { CouponIdList, Modals, Pages } from '../../../types/enums';
import { TimedMessage } from '../TimedMessage/timed-message';
import { ProfileService } from '../../../services/profile-service';
import { ICalorie } from '../../../online-ordering/interfaces/online-ordering.interface';
import { IToggleButtonData, IToggleButtonOptionData } from '../ToggleButton/toggle-button.interface';
import { IApplyCouponByIdParamsPartial } from '@ts/models/coupons.interface';
import { ToggleButton } from '../ToggleButton/toggle-button';

export interface IMenuItemCartCardUiParams {
    parentDivId: string,
    eventBus: EventEmitter,
    data: Data,
    context: Context,
    localizationService: LocalizationService,
    order: Order,
    dataLayerService: DataLayerService,
    loyaltyProvider: ILoyaltyProvider,
    profileService: ProfileService
}

interface IMenuItemCartCard {
    showOptions: boolean,
    keepExpanded: boolean
}

/**
 * Class representing a cart card for menu items in an order.
 * Responsible for rendering and managing menu item cards in the cart.
 */
export abstract class MenuItemCartCardBase {
    protected readonly _parentDiv: string;
    protected readonly _eventBus: EventEmitter;
    protected readonly _data: Data;
    protected readonly _context: Context;
    protected readonly _localizationService: LocalizationService;
    protected readonly _order: Order;
    protected readonly _dataLayer: DataLayerService;
    protected readonly _loyaltyProvider: ILoyaltyProvider;
    protected readonly _profileService: ProfileService;

    protected readonly _state: IMenuItemCartCard;

    /**
     * Creates an instance of MenuItemCartCard.
     * @param {string} parentDivId - The ID of the parent div where the menu item cards will be rendered.
     * @param {Data} data - The data object containing application-level data.
     * @param {Order} order - The order object representing the current order.
     * @param {DataLayerService} dataLayer - The data layer service for managing application state.
     * @param {ILoyaltyProvider} loyaltyProvider - The loyalty provider instance for handling loyalty rewards.
     * @param {ProfileService} profileService - The profile service for managing user profiles.
     */
    protected constructor(params: IMenuItemCartCardUiParams) {
        this._parentDiv = params.parentDivId;
        this._eventBus = params.eventBus;
        this._data = params.data;
        this._context = params.context;
        this._localizationService = params.localizationService;
        this._order = params.order;
        this._dataLayer = params.dataLayerService;
        this._loyaltyProvider = params.loyaltyProvider;
        this._profileService = params.profileService;

        this._state = {
            showOptions: false,
            keepExpanded: false
        };
    }

    /**
     * Initializes the menu item cart card by rendering the components.
     * @param {boolean} showOptions - Indicates whether to show options for each menu item card.
     * @param {boolean} keepExpanded - Indicates whether to keep the modifiers expanded by default.
     */
    public init(showOptions: boolean, keepExpanded: boolean = false): void {
        this._state.showOptions = showOptions;
        this._state.keepExpanded = keepExpanded;
        this.render();
    }

    /**
     * Renders the menu item cart card components.
     * @private
     */
    public readonly render = (): void => {
        this._createItemsCards();
    }

    /**
    * Creates the item cards for the order and appends them to the parent div.
    * @private
    */
    private _createItemsCards(): void {
        const parent = document.getElementById(this._parentDiv);
        if (!parent) return;

        parent.innerHTML = "";

        const items = this._order.getProperty("Items");
        if (!items.length) return;

        const groupedItems = this._groupItems(items);
        this.renderItems(parent, groupedItems);
    }

    /* =============================
      Template methods
   ============================== */

    protected _afterCardRender(
        _item: IOrderItem,
        _card: HTMLElement
    ): void {
        // optional hook
    }

    /**
     * Generates the HTML for a menu item card in the cart.
     * @protected
     * @param {IOrderItem[]} items - The group of items to display in the card.
     * @returns {HTMLElement | null} - The generated HTML element for the item card, or null if the item is invalid.
     */
    protected abstract _generateItemCardHtml(items: IOrderItem[]): HTMLElement | null;

    /**
     * Generates the header HTML for a cart item, including title, size,
     * crust, and total price.
     *
     * Handles half-and-half items and applies localization and currency
     * formatting based on store settings.
     *
     * @param items - Order items representing the same menu item.
     * @param menuItem - Menu item metadata.
     * @returns Rendered header HTML template.
     */
    protected abstract _generateHeaderTemplate(items: IOrderItem[], menuItem: IDataItem): string;

    protected abstract _generateItemPointsTemplate(items: IOrderItem[]): string;

    /**
     * Generates the HTML for taxes displayed next to the item.
     * @private
     * @param {IOrderItem} item - The specific order item.
     * @returns {string} - The HTML for taxes.
     */
    protected abstract _generateTaxesTemplate(item: IOrderItem): string;

    /**
     * Generates the HTML template for special instructions on an order item.
     *
     * Returns an empty string when no instructions are present.
     *
     * @param item - Order item containing special instructions.
     * @returns Rendered instructions HTML or an empty string.
     */
    protected abstract _generateInstructionTemplate(item: IOrderItem): string;

    /**
     * Generates the HTML for special instructions for the item.
     * @protected
     * @param {IOrderItem} item - The specific order item.
     * @returns {string} - The HTML for special instructions.
     */
    protected abstract _generateInstructionList(item: IOrderItem): string[];

    /* =============================
       Shared helpers
    ============================== */

    protected renderItems(
        parent: HTMLElement,
        groupedItems: Array<{ itemKey: number; items: IOrderItem[] }>
    ): void {
        for (const [index, group] of groupedItems.entries()) {
            const card = this._generateItemCardHtml(group.items);
            if (!card) return;

            this._attachCommonListeners(group.items[0], card, index);
            this._afterCardRender(group.items[0], card);
            parent.appendChild(card);
        }
    }

    protected _attachCommonListeners(
        orderItem: IOrderItem,
        card: HTMLElement,
        index: number
    ): void {
        if (!this._state.showOptions)
            return;

        this._bindClick(card, `#btn_cart_quantity_remove_${orderItem.ItemKey}`, async () => {
            Util.resetAnnounceCycle();
            await this._decreaseItemQuantity(orderItem);
        });

        this._bindClick(card, `#btn_cart_quantity_add_${orderItem.ItemKey}`, async () => {
            Util.resetAnnounceCycle();
            await this._increaseItemQuantityAsync(orderItem);
        });

        this._bindClick(card, `#btn_cart_customize_${orderItem.ItemKey}`, (event: Event) => {
            this._editItem(orderItem.ItemKey, index);

            const btnText = (event.target as HTMLButtonElement)?.innerText ?? "";
            this._dataLayer?.customize_item(
                btnText,
                window.location.href,
                Pages.CART.replace("/", "")
            );
        });

        this._bindClick(card, `#btn_cart_remove_${orderItem.ItemKey}`, async () => {
            await this._removeItemFromOrderAsync(orderItem);
        });
    }

    protected _bindClick(
        root: HTMLElement,
        selector: string,
        handler: (e: Event) => void | Promise<void>
    ): void {
        const el = root.querySelector(selector) as HTMLElement | null;
        if (el) el.onclick = handler;
    }

    /**
     * Generates the HTML for the item card options (e.g., quantity, customize, remove).
     * @protected
     * @param {IOrderItem} item - The specific order item.
     * @returns {string} - The HTML for the options section.
     */
    protected generateOptionsTemplate(item: IOrderItem): string {
        const isRedeemed = item.IsRedeemed;
        const increaseQuantityAriaLabel = this._localizationService.names("ARIA_INCREASE_QUANTITY");
        const decreaseQuantityAriaLabel = this._localizationService.names("ARIA_DECREASE_QUANTITY");
        return `
             <footer class="menu-item-cart-card-actions">
                <div class="item-quantity ${isRedeemed ? "quantity-disabled" : ""}" role="group" aria-label="${this._localizationService.names("ARIA_QUANTITY_SELECTOR")}">
                    <button
                        id="btn_cart_quantity_remove_${item.ItemKey}"
                        class="quantity-btn" aria-label="${decreaseQuantityAriaLabel}"
                        ${isRedeemed ? 'disabled' : ''}
                    >
                        <span class="${isRedeemed ? 'icon-lock' : 'icon-subtract'}" aria-hidden="true"></span>
                    </button>
                    <span id="spn_cart_quantity_${item.ItemKey}" aria-live="polite" aria-atomic="true" class="quantity-number">${item.Quantity}</span>
                    <button 
                        id="btn_cart_quantity_add_${item.ItemKey}"
                        class="quantity-btn" aria-label="${increaseQuantityAriaLabel}"
                        ${isRedeemed ? 'disabled' : ''}
                    >
                        ${isRedeemed ? "" : '<span class="icon-plus" aria-hidden="true"></span>'}
                    </button>
                </div>
                <div class="item-options">
                    <button id="btn_cart_customize_${item.ItemKey}" class="button-link customize-btn" aria-describedby="cart-item-title-${item.ItemKey}">${this._localizationService.names("Customize")}</button>
                    <button id="btn_cart_remove_${item.ItemKey}" class="button-link remove-btn" aria-describedby="cart-item-title-${item.ItemKey}">${this._localizationService.names("Remove")}</button>
                </div>
            </footer>`;
    }

    /**
     * Calculates the price for an item, considering whether it is a half-and-half item.
     * @private
     * @param {IOrderItem} item - The item for which the price is calculated.
     * @param {boolean} isHalfHalf - Indicates if the item is a half-and-half item.
     * @returns {number} - The calculated price.
     */
    protected calculateItemPrice(item: IOrderItem, isHalfHalf: boolean): number {
        let itemPrice = isHalfHalf ? item.HalfHalfPrice : item.Price;

        if (item.IsRedeemed) {
            const appliedRedeemCoupon = this._getAppliedLoyaltyRedeemCoupon([item]);

            if (appliedRedeemCoupon) {
                itemPrice -= Math.min(itemPrice, appliedRedeemCoupon.Amount);
            }
        }
        return itemPrice;
    }

    /**
     * Retrieves the calories for the item.
     * @private
     * @param {any} menuItem - The menu item data.
     * @param {IOrderItem} item - The specific order item.
     * @returns {IName[]} - The localized calorie information.
     */
    protected getItemCalories(menuItem: IDataItem, item: IOrderItem): IName[] {
        return this.getItemCaloriesUtil(
            this._localizationService,
            menuItem,
            item,
            this._order.getProperty("OrderTypeID"),
            item.SizeId,
            this._data.getProperty("DefaultPreModifierId"),
            this._data.getProperty("Settings").DSZID
        );
    }

    protected getItemCaloriesUtil(localizationService: LocalizationService, mItem: IDataItem, oItem: IOrderItem | null, orderTypeID: number, sizeID: number | null, defaultPreModifierId: number, defaultSizeId: number): IName[] {
        const servingName = (mItem.ServingNames) ? mItem.ServingNames : null;

        if (sizeID == null) {
            const sizes = OnlineOrderingUtil.GetItemSizes(mItem, orderTypeID);
            if (sizes?.length === 1 && sizes[0].SizeId === 0)
                sizeID = sizes[0].SizeId;
            else
                sizeID = OnlineOrderingUtil.GetItemDefaultSize(mItem, orderTypeID, null, defaultSizeId).SizeId;
        }

        let modCals = 0;
        if (!oItem)
            modCals = OnlineOrderingUtil.getMenuItemModifiersCalories(mItem, sizeID, defaultPreModifierId);
        else
            modCals = OnlineOrderingUtil.getOrderItemModifiersCalories(mItem, oItem.Modifiers, sizeID, defaultPreModifierId);

        let calorieObj: ICalorie | null = null;
        const serveCals = OnlineOrderingUtil.getItemSizeMinMaxCalories(mItem, sizeID);
        if (serveCals.minCal !== 0 && serveCals.maxCal !== 0 && serveCals.minCal !== serveCals.maxCal) {
            if (mItem.CalorieDisplayId == 1) // per serving
                calorieObj = {
                    Name: "CalorieServingMinMax",
                    MinCal: Math.trunc((serveCals.minCal + modCals) / serveCals.serves),
                    MaxCal: Math.trunc((serveCals.maxCal + modCals) / serveCals.serves),
                    Serving: null,
                    ServingName: servingName
                };
            else if (mItem.CalorieDisplayId == 2) // fraction 
                calorieObj = {
                    Name: "CalorieFractionMinMax",
                    MinCal: Math.trunc((serveCals.minCal + modCals) / serveCals.serves),
                    MaxCal: Math.trunc((serveCals.maxCal + modCals) / serveCals.serves),
                    Serving: serveCals.serves,
                    ServingName: servingName
                };
        } else if (serveCals.minCal + modCals > 0) {
            if (mItem.CalorieDisplayId == 1) // per serving
                calorieObj = {
                    Name: "CalorieServingMin",
                    MinCal: Math.trunc((serveCals.minCal + modCals) / serveCals.serves),
                    MaxCal: null,
                    Serving: null,
                    ServingName: servingName
                };
            else if (mItem.CalorieDisplayId == 2) // fraction 
                calorieObj = {
                    Name: "CalorieFractionMin",
                    MinCal: Math.trunc((serveCals.minCal + modCals) / serveCals.serves),
                    MaxCal: null,
                    Serving: serveCals.serves,
                    ServingName: servingName
                };
        }

        if (calorieObj) {
            const languages = localizationService.getSupportedLanguages();
            const nameObj: IName[] = [];
            for (const lang of languages) {
                const serving = calorieObj.ServingName ? this._localizationService.GetName(calorieObj.ServingName, lang) : localizationService.names("Slice", lang);
                switch (calorieObj.Name) {
                    case "CalorieServingMinMax":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", (calorieObj.MaxCal || "").toString()).replace("????", serving) });
                        break;
                    case "CalorieFractionMinMax":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", (calorieObj.MaxCal || "").toString()).replace("????", (calorieObj.Serving || "").toString()).replace("?????", serving) });
                        break;
                    case "CalorieServingMin":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", serving) });
                        break;
                    case "CalorieFractionMin":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", (calorieObj.Serving || "").toString()).replace("????", serving) });
                        break;
                }
            }
            return nameObj;
        }

        return [];
    }

    /**
     * Localizes tax names with their amounts for supported languages.
     * @private
     * @param {IName[]} taxNameArray - The array of tax names.
     * @param {number} taxAmount - The tax amount.
     * @returns {IName[]} - The localized tax names.
     */
    protected localizeTaxNames(taxNameArray: IName[], taxAmount: number): IName[] {
        const country = this._data.getProperty("StoreInfo").country;

        return this._localizationService.getSupportedLanguages().map((lang) => {
            const taxDef = taxNameArray.find(x => x.CULT.toLowerCase() === lang.toLowerCase());
            return {
                CULT: lang,
                NAM: taxDef ? `${taxDef.NAM} ${Util.formatMoney(taxAmount, country)}` : `Unknown ${Util.formatMoney(taxAmount, country)}`
            };
        });
    }

    /**
     * Groups the items by their unique item key.
     * @private
     * @param {IOrderItem[]} items - The list of items to group.
     * @returns {Array<{ itemKey: number, items: IOrderItem[] }>} - Grouped items by item key.
     */
    private _groupItems(items: IOrderItem[]): Array<{ itemKey: number; items: IOrderItem[] }> {
        const map = new Map<number, IOrderItem[]>();

        for (const item of items) {
            const list = map.get(item.ItemKey) ?? [];
            list.push(item);
            map.set(item.ItemKey, list);
        }

        return Array.from(map.entries()).map(([itemKey, items]) => ({ itemKey, items }));
    }

    /**
     * Increases the quantity of an item in the order by 1.
     * @private
     * @param {IOrderItem} item - The item to increase the quantity for.
     * @returns {Promise<void>} - A promise that resolves when the quantity has been updated.
     */
    private readonly _increaseItemQuantityAsync = async (item: IOrderItem): Promise<void> => {
        if (item.IsRedeemed) return;

        const newQuantity = item.Quantity + 1;
        await this._order.changeQuantityByItemKeyAsync(this._loyaltyProvider, item.ItemKey, newQuantity);
        Util.announce(`Quantity increased to ${newQuantity}`);
    }

    /**
     * Decreases the quantity of an item in the order by 1, unless the quantity is already 1.
     * @private
     * @param {IOrderItem} item - The item to decrease the quantity for.
     * @returns {Promise<void>} - A promise that resolves when the quantity has been updated.
     */
    private readonly _decreaseItemQuantity = async (item: IOrderItem): Promise<void> => {
        if (item.Quantity === 1)
            return;

        if (item.IsRedeemed) return;

        const newQuantity = item.Quantity - 1;
        await this._order.changeQuantityByItemKeyAsync(this._loyaltyProvider, item.ItemKey, newQuantity);
        Util.announce(`Quantity decreased to ${newQuantity}`);
    }

    /**
     * Opens the customization dialog for an item in the order.
     * @private
     * @param {number} itemKey - The key of the item to customize.
     */
    private readonly _editItem = (itemKey: number, itemIndex: number): void => {
        const oItems = this._order.getOrderItemsByItemKey(itemKey);
        if (oItems.length === 0)
            return;

        const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItems[0].ItemId);
        if (!mItem)
            return;

        if (this._dataLayer) {
            this._dataLayer.select_item(this._order, oItems[0], itemIndex, Pages.CART.replace('/', ''));
        }

        const props: ICustomizeItemProps = {
            sizeId: oItems[0].SizeId,
            quantity: oItems[0].Quantity,
            mealDeal: null,
            recentOrder: false,
            mItem: mItem,
            itemsToEdit: oItems
        };

        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CUSTOMIZE_ITEM, props);
    }

    /**
     * Removes an item from the order.
     * @private
     * @param {number} orderItem - The key of the item to remove.
     * @returns {Promise<void>} - A promise that resolves when the item has been removed.
     */
    private readonly _removeItemFromOrderAsync = async (orderItem: IOrderItem): Promise<void> => {
        const name = this.getOrderItemName(orderItem);
        const msg = this._localizationService.names("Cart_RemovedItemNotice").replace("{{item_name}}", name);
        const screenReaderMsg = this._localizationService.names("Cart_ScreenReader_RemovedItemNotice").replace("{{item_name}}", name);

        const timedMessage = new TimedMessage(msg, undefined, 1000, screenReaderMsg);
        timedMessage.show();

        await this._order.deleteItemByItemKeyAsync(orderItem.ItemKey, this._loyaltyProvider);
    }

    /**
     * Resolves the localized display name for an order item.
     *
     * @param orderItem - Order item to resolve the name for.
     * @returns Localized item name, or an empty string if not found.
     */
    protected getOrderItemName(orderItem: IOrderItem): string {
        const menuItem = this._data.getProperty("Items").find(x => x.ItemId === orderItem.ItemId);

        if (!menuItem)
            return "";

        return this._localizationService.GetName(menuItem.Names);
    }

    protected _generateRedeemToggleElement(items: IOrderItem[]): HTMLElement | null {
        if (!this._shouldShowLoyaltyItemOptions(items)) return null;

        const itemIsRedeemed = this._itemIsRedeemedForPoints(items);

        const pointsUnitText = this._localizationService.getLoyaltyUnitName(this._data.getLoyaltyInfo());
        const toggleOnText = this._localizationService.names("REDEEM_WITH_POINTS_TEMPLATE")
            .replaceAll("{{points_unit}}", pointsUnitText);

        const toggleData: IToggleButtonData = {
            toggleName: `cart_item_toggle_${items[0].ItemKey}`,
            toggleOffOption: {
                label: this._localizationService.names("PayWithDollars"),
                selected: !itemIsRedeemed,
                function: () => this._payWithDollarsToggleButton(items),
                announcementOnSelect: `${this._localizationService.names("PayWithDollars")} ${this._localizationService.names("SELECTED")}`
            } as IToggleButtonOptionData,
            toggleOnOption: {
                label: toggleOnText,
                selected: itemIsRedeemed,
                function: () => this._redeemForPointsToggleButton(items),
                announcementOnSelect: `${toggleOnText} ${this._localizationService.names("SELECTED")}`
            } as IToggleButtonOptionData,
            locked: !itemIsRedeemed && this._hasInsufficientPointsToRedeem(items),
            isToggled: itemIsRedeemed,
            announcementOnFocus: this._localizationService.names("ARIA_LOYALTY_REDEEM_TOGGLE"),
            announcementOnFocusLocked: this._localizationService.names("ARIA_LOYALTY_REDEEM_TOGGLE_LOCKED"),
        };

        const toggleButton = new ToggleButton(toggleData);
        const html = `<div class="menu-item-cart-card-redeem-toggle-container"></div>`;
        const container = Util.createHtmlElementFromTemplate(html);
        container.appendChild(toggleButton.element);
        return container;
    }

    protected _shouldShowLoyaltyItemOptions(orderItem: IOrderItem[]): boolean {
        if (!this._profileService.user.isLoggedIn()) return false;

        if (!this._loyaltyProvider.hasPointRedemption()) return false;

        const loyaltyItems = this._data.getProperty("LoyaltyItems");
        const itemIsIneligible = orderItem.some(
            (item) => OnlineOrderingUtil.GetLoyaltyPoints(item.ItemId, item.SizeId, loyaltyItems) === null,
        );

        if (itemIsIneligible) return false;

        return true;
    }

    private _itemIsRedeemedForPoints(items: IOrderItem[]): boolean {
        return items.some(item => item.IsRedeemed === true);
    }

    private _hasInsufficientPointsToRedeem(items: IOrderItem[]): boolean {
        const userAvailablePoints = this._loyaltyProvider.getUserAvailableLoyaltyPoints();
        const itemPointsCost = this._getItemPointsCost(items);
        return itemPointsCost > userAvailablePoints;
    }

    private _getItemPointsCost(items: IOrderItem[]): number {
        const firstHalfPoints = OnlineOrderingUtil.GetLoyaltyPoints(
            items[0].ItemId,
            items[0].SizeId,
            this._data.getProperty("LoyaltyItems"),
        );

        const secondHalfPoints =
            items.length > 1
                ? OnlineOrderingUtil.GetLoyaltyPoints(
                      items[1].ItemId,
                      items[1].SizeId,
                      this._data.getProperty("LoyaltyItems"),
                  )
                : 0;

        const quantity = items[0].Quantity;

        const points = Math.max(firstHalfPoints ?? 0, secondHalfPoints ?? 0) * quantity;
        return points;
    }

    protected readonly _payWithDollarsToggleButton = (items: IOrderItem[]): void => {
        items.forEach(item => item.IsRedeemed = false);

        const coupon = this._getAppliedLoyaltyRedeemCoupon(items);

        if (coupon) {
            this._order.removeCouponByKeyAsync(this._loyaltyProvider, coupon.CouponKey, true, true);
        }
    };

    protected readonly _redeemForPointsToggleButton = async (items: IOrderItem[]): Promise<void> => {
        items.forEach(item => item.IsRedeemed = true);

        await this._removeLoyaltyExcludedCouponsAsync(items);

        const discountAmount = this._computeRedeemDiscountAmount(items[0]);

        const couponParams = {
            couponId: CouponIdList.LOYALTY_REDEEM_ITEM,
            discount: discountAmount,
            couponCode: String(this._getItemPointsCost(items)),
            itemKey: items[0].ItemKey,
            loyaltyProvider: this._loyaltyProvider
        } as IApplyCouponByIdParamsPartial

        await this._order.applyCouponByIdAsync(couponParams)
    };

    protected _computeRedeemDiscountAmount(item: IOrderItem): number {
        const currentDiscountsApplied = this._getCurrentDiscountApplied(item.ItemKey);

        const loyaltyItems = this._data.getProperty("LoyaltyItems");
        const isHalfHalf = item.HalfCount > 1;

        const price = isHalfHalf
            ? OnlineOrderingUtil.GetRedeemItemChargeableHalfPrice(item.ItemKey, this._order.getProperty("Items"), loyaltyItems)
            : OnlineOrderingUtil.GetRedeemItemChargeablePrice(item, loyaltyItems);

        return (price - currentDiscountsApplied) * item.Quantity;
    }

    protected _getCurrentDiscountApplied(itemKey: number): number {
        const currentAppliedCouponsAmount = this._order.getProperty("ItemsCoupons")
            .filter(x => x.ItemKey === itemKey)
            .reduce((acc, c) => acc + c.AfterTaxDiscount + c.BeforeTaxDiscount, 0);

        return currentAppliedCouponsAmount;
    }

    private async _removeLoyaltyExcludedCouponsAsync(items: IOrderItem[]): Promise<void> {
        for (const couponKey of this._getLoyaltyExcludedCouponKeys(items)) {
            await this._order.removeCouponByKeyAsync(this._loyaltyProvider, couponKey, true, true);
        }
    }

    /** Keys of applied coupons attached to these items that forbid loyalty redemption. */
    private _getLoyaltyExcludedCouponKeys(items: IOrderItem[]): number[] {
        const itemKeys = new Set(items.map(item => item.ItemKey));
        const attachedCouponKeys = new Set(
            this._order.getProperty("ItemsCoupons")
                .filter(itemCoupon => itemKeys.has(itemCoupon.ItemKey))
                .map(itemCoupon => itemCoupon.CouponKey)
        );

        return this._order.getProperty("Coupons")
            .filter(coupon => coupon.ExcludeLoyaltyUsage && attachedCouponKeys.has(coupon.CouponKey))
            .map(coupon => coupon.CouponKey);
    }

    protected _getAppliedLoyaltyRedeemCoupon(items: IOrderItem[]): IOrderCoupon | null {
        const coupons: IOrderItemCoupon[] = this._order.getProperty("ItemsCoupons").filter(c => c.ItemKey === items[0].ItemKey);
        const coupon = this._order
            .getProperty("Coupons")
            .find((c) =>
                coupons.some(
                    (ic) => ic.CouponKey === c.CouponKey && c.CouponId === CouponIdList.LOYALTY_REDEEM_ITEM,
                ),
            );
        return coupon ?? null;
    }

    protected _addRedeemToggleButtonToCard(card: HTMLElement, items: IOrderItem[]): void {
        if (this._state.showOptions && this._shouldShowLoyaltyItemOptions(items)) {
            const redeemItemToggleButton = this._generateRedeemToggleElement(items);

            if (!redeemItemToggleButton) return;

            const optionsElement = card.querySelector(".menu-item-cart-card-actions");
            optionsElement?.before(redeemItemToggleButton);
        }
    }

    protected _generateItemQuantityTemplate(item: IOrderItem): string {
        const quantityText = `${this._localizationService.names("Quantity")}: ${item.Quantity}`;
        return `
            <div class="item-quantity-display">
                <span class="quantity-number">${quantityText}</span>
            </div>
        `;
    }
}